
import { useInView } from '../utils/animations';
import { MessageCircle, Video, FileText, Calendar, Camera, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useIsMobile } from '../hooks/use-mobile';

const steps = [
  {
    id: 1,
    title: "Formularz kontaktowy",
    description: "Wypełnij formularz, a my sprawdzimy dostępność terminu.",
    icon: <MessageCircle size={20} strokeWidth={1.5} className="text-elegant-900" />
  },
  {
    id: 2,
    title: "Spotkanie online",
    description: "Rozmawiamy przy kawie o Waszym ślubie i naszej pracy. To luźna pogadanka, dzięki której lepiej się poznamy.",
    icon: <Video size={20} strokeWidth={1.5} className="text-elegant-900" />
  },
  {
    id: 3,
    title: "Umowa i zadatek",
    description: "Podpisujemy umowę i wpłacacie zadatek. To oficjalna rezerwacja terminu dla Was.",
    icon: <FileText size={20} strokeWidth={1.5} className="text-elegant-900" />
  },
  {
    id: 4,
    title: "Ostatnie ustalenia",
    description: "Miesiąc przed ślubem dopinamy szczegóły. Ustalamy harmonogram i plan działania.",
    icon: <Calendar size={20} strokeWidth={1.5} className="text-elegant-900" />
  },
  {
    id: 5,
    title: "Ślub i wesele",
    description: "Spotykamy się w dniu ślubu. Fotografujemy, filmujemy i tworzymy Wasze wspomnienia.",
    icon: <Camera size={20} strokeWidth={1.5} className="text-elegant-900" />
  }
];

const Process = () => {
  const [titleRef, titleInView] = useInView({ threshold: 0.1 });
  const [stepsRef, stepsInView] = useInView({ threshold: 0.1 });
  const isMobile = useIsMobile();
  
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <section id="process" className={`section bg-white ${isMobile ? 'py-10' : ''}`}>
      <div className="container-custom">
        <div className={`text-center max-w-2xl mx-auto ${isMobile ? 'mb-6' : 'mb-12'}`}>
          <h2 
            ref={titleRef as any}
            className={`section-title ${isMobile ? 'text-2xl' : 'text-3xl md:text-4xl'} mb-2 ${
              titleInView ? 'opacity-100 translate-y-0 transition-all duration-700' : 'opacity-0 translate-y-10 transition-all duration-700'
            }`}
          >
            Rezerwacja - proces
          </h2>
          <p 
            className={`text-elegant-500 font-sans font-light uppercase tracking-wider ${isMobile ? 'text-xs' : 'text-sm md:text-base'} ${
              titleInView ? 'opacity-100 translate-y-0 transition-all duration-700 delay-100' : 'opacity-0 translate-y-10 transition-all duration-700'
            }`}
          >
            W jaki sposób zarezerwować termin na fotoreportaż ślubny i film?
          </p>
        </div>
        
        <div 
          ref={stepsRef as any}
          className={`relative ${
            stepsInView ? 'opacity-100 translate-y-0 transition-all duration-700 delay-200' : 'opacity-0 translate-y-10 transition-all duration-700'
          }`}
        >
          {isMobile ? (
            <div className="px-2">
              <div className="relative border-l-2 border-elegant-200 pl-6 ml-4">
                {steps.map((step, index) => (
                  <div key={step.id} className="mb-4 last:mb-0 relative">
                    <div className="absolute left-0 top-0 -translate-x-[calc(100%+0.75rem)] w-6 h-6 bg-elegant-100 rounded-full border-2 border-elegant-200 flex items-center justify-center">
                      <span className="text-xs font-medium">{step.id}</span>
                    </div>
                    
                    <div>
                      <div className="flex items-center mb-1">
                        <div className="w-5 h-5 rounded-full bg-elegant-100 shadow-sm text-elegant-900 flex items-center justify-center mr-2">
                          {step.icon}
                        </div>
                        <h3 className="text-sm font-serif text-elegant-900">{step.title}</h3>
                      </div>
                      <p className="text-xs text-elegant-600 mb-1">{step.description}</p>
                      
                      {step.id === 1 && (
                        <Button 
                          variant="transparent" 
                          size="sm" 
                          className="font-medium mt-1 text-navy-800 text-xs h-7 px-3 border-navy-800"
                          onClick={scrollToContact}
                        >
                          Zapytaj o termin <ArrowRight size={12} className="ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-elegant-200 hidden md:block"></div>
              
              {steps.map((step, index) => (
                <div 
                  key={step.id}
                  className={`relative flex flex-col md:flex-row ${index % 2 === 0 ? 'md:flex-row-reverse' : ''} mb-12 last:mb-0`}
                  style={{ transitionDelay: `${index * 100 + 200}ms` }}
                >
                  <div className="flex-1 md:px-10">
                    <div className={`md:max-w-md ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}>
                      <div className="flex items-center mb-3">
                        <div className="w-9 h-9 rounded-full bg-elegant-100 shadow-sm text-elegant-900 flex items-center justify-center mr-3">
                          {step.icon}
                        </div>
                        <span className="font-serif text-lg text-elegant-900">Krok {step.id}</span>
                      </div>
                      <h3 className="text-lg md:text-xl font-serif text-elegant-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-elegant-600 mb-3">{step.description}</p>
                      
                      {step.id === 1 && (
                        <Button 
                          variant="transparent" 
                          size="sm" 
                          className="font-medium mt-2 text-navy-800 border-navy-800"
                          onClick={scrollToContact}
                        >
                          Zapytaj o termin i ofertę <ArrowRight size={16} className="ml-1" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="absolute left-1/2 top-0 transform -translate-x-1/2 w-3 h-3 bg-elegant-900 rounded-full mt-3 hidden md:block"></div>
                  
                  <div className="flex-1 md:px-10"></div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Process;
